import {Place} from "./Place"
import * as React from "react"
import {Locales} from "./Locales"
import {Form, List} from "semantic-ui-react"
import {Place as PlaceType} from "../model/places/PlaceTypes"
import {AirportStopForm, emptyAirportState} from "./AirportStop"
import {Locale} from "../model/BookingTypes"
import {PlaceSuggest} from "./PlaceSuggest"
import {arrayDelete, containsDuplicates} from "../tsutil"
import {BookingFormAirportState, BookingFormJourneyState} from "../state"
import {WithModalStore} from "../App"
import {MapState} from "./BookingDetails"
import {FlightTypeEnum} from "../graphql/queries"

enum JourneyFormStage {
  Origin,
  Destination,
  Waypoint,
}

interface PlaceState {
  journey: BookingFormJourneyState
  airportPickup: BookingFormAirportState
  airportDropoff: BookingFormAirportState
  locale: Locale
}

export function hasCompleteJourney(state: PlaceState) {
  const {journey, airportPickup, airportDropoff} = state
  return (
    journey.origin &&
    journey.destination &&
    // If origin is an airport stop, they must have a flight number and airline code
    (!journey.origin.isAirportStop || (airportPickup.flightNumber && airportPickup.airlineCode)) &&
    // If destination is an airport stop, they must have an airline code
    (!journey.destination.isAirportStop || airportDropoff.airlineCode)
  )
}

function stageLabel(stage: JourneyFormStage) {
  switch (stage) {
    case JourneyFormStage.Origin:
      return "From"
    case JourneyFormStage.Destination:
      return "To"
    case JourneyFormStage.Waypoint:
      return "Extra stop"
  }
}

interface Props extends WithModalStore {
  state: PlaceState
  mapState: MapState<PlaceState> | null
  onOptimizeWaypoints: (() => void) | null
  wantFlightNumber: boolean
  disabled: boolean
}

interface State {
  addingStop: boolean
}

export class PlaceFields extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props)

    this.state = {
      addingStop: false,
    }
  }

  canOptimize = () => {
    const {
      state: {
        journey: {origin, destination, waypoints},
      },
    } = this.props

    return origin && destination && waypoints.length > 1 && !containsDuplicates(waypoints)
  }

  handleAddStop = () => this.setState({addingStop: true})

  handleNop = () => null

  handlePlaceSelect = (stage: JourneyFormStage) => (suggestion: PlaceType) => {
    onPlaceSelect(this.props.mapState!, stage, suggestion)
    if (this.state.addingStop) {
      this.setState({addingStop: false})
    }
  }

  render() {
    const {
      modalStore,
      state: {
        journey,
        journey: {origin, destination, waypoints},
        airportPickup,
        airportDropoff,
        locale,
      },
      onOptimizeWaypoints,
      wantFlightNumber,
      disabled,
      mapState,
    } = this.props

    const {addingStop} = this.state

    const stage = journeyFormStage(journey)
    const label = stageLabel(stage)
    const edit = mapState !== null
    const okAirportPickup = Boolean(
      (wantFlightNumber ? airportPickup.flightNumber : true) && airportPickup.airlineCode,
    )
    const needsAirportPickup = Boolean(origin && origin.isAirportStop)
    const needsAirportDropoff = Boolean(destination && destination.isAirportStop)

    const pickupOk = !needsAirportPickup || okAirportPickup
    const showPlaceSuggest = edit && pickupOk
    const showAddStopButton = origin && destination && !addingStop && pickupOk && edit
    const originPlaceSuggest = showPlaceSuggest && !origin
    const destinationPlaceSuggest = showPlaceSuggest && origin && !destination
    const waypointPlaceSuggest = !showAddStopButton && showPlaceSuggest && origin && destination

    const placeSuggest = (
      <Form.Field>
        <label>{label}</label>
        <PlaceSuggest
          disabled={disabled}
          placeholder={label}
          onSelect={this.handlePlaceSelect(stage)}
          locale={locale}
          showPopupHelp={stage === JourneyFormStage.Origin}
        />
      </Form.Field>
    )

    return (
      <>
        {mapState && (
          <Form.Group>
            <Form.Field>
              <label>State</label>
              <Locales
                disabled={disabled}
                locale={locale}
                onLocale={(locale) => {
                  mapState((state) => ({...state, locale}))
                }}
              />
            </Form.Field>
          </Form.Group>
        )}
        {origin && (
          <Form.Group>
            <Form.Field>
              <label>From</label>
              <Place
                modalStore={modalStore}
                place={origin}
                disabled={disabled}
                onDelete={
                  mapState &&
                  (() => {
                    mapState((state) => ({
                      ...state,
                      airportPickup: emptyAirportState(),
                      journey: {...state.journey, origin: null},
                    }))
                  })
                }
                refetchLocaleId={locale.id}
              />
            </Form.Field>
          </Form.Group>
        )}
        {originPlaceSuggest && placeSuggest}
        {needsAirportPickup &&
          origin && (
            <AirportStopForm
              flightNumberRequired={true}
              wantFlightNumber={wantFlightNumber}
              state={airportPickup}
              mapState={
                mapState &&
                ((fn) =>
                  mapState((state) => ({
                    ...state,
                    airportPickup: fn(state.airportPickup),
                  })))
              }
              disabled={disabled}
              airportPlaceId={origin.id}
            />
          )}
        {destination && (
          <Form.Group>
            <Form.Field>
              <label>To</label>
              <Place
                modalStore={modalStore}
                onDelete={
                  mapState &&
                  (() => {
                    mapState((state) => ({
                      ...state,
                      airportDropoff: emptyAirportState(),
                      journey: {...state.journey, destination: null},
                    }))
                  })
                }
                refetchLocaleId={locale.id}
                place={destination}
                disabled={disabled}
              />
            </Form.Field>
          </Form.Group>
        )}
        {destinationPlaceSuggest && placeSuggest}
        {needsAirportDropoff &&
          destination && (
            <AirportStopForm
              flightNumberRequired={false}
              wantFlightNumber={wantFlightNumber}
              state={airportDropoff}
              mapState={
                mapState &&
                ((fn) =>
                  mapState((state) => ({
                    ...state,
                    airportDropoff: fn(state.airportDropoff),
                  })))
              }
              airportPlaceId={destination.id}
              disabled={disabled}
            />
          )}
        {waypoints.length > 0 && (
          <Form.Group>
            <Form.Field>
              <label>Via</label>
              <List
                items={waypoints.map((place, index) => (
                  <Place
                    modalStore={modalStore}
                    key={index}
                    place={place}
                    onDelete={
                      mapState &&
                      (() => {
                        mapState((state) => ({
                          ...state,
                          journey: {
                            ...state.journey,
                            waypoints: arrayDelete(state.journey.waypoints, index),
                          },
                        }))
                      })
                    }
                    refetchLocaleId={locale.id}
                    disabled={disabled}
                  />
                ))}
              />
            </Form.Field>
          </Form.Group>
        )}
        {showAddStopButton && (
          <Form.Group>
            <Form.Button
              icon="add"
              labelPosition="left"
              disabled={disabled}
              onClick={disabled ? this.handleNop : this.handleAddStop}
              content="Add stop"
            />
            {this.canOptimize() &&
              onOptimizeWaypoints && (
                <Form.Button
                  disabled={disabled}
                  onClick={(e) => {
                    e.preventDefault()
                    onOptimizeWaypoints()
                  }}
                  content="Optimize stop order"
                />
              )}
          </Form.Group>
        )}
        {waypointPlaceSuggest && placeSuggest}
      </>
    )
  }
}

function journeyFormStage(journey: BookingFormJourneyState) {
  if (!journey.origin) {
    return JourneyFormStage.Origin
  } else if (!journey.destination) {
    return JourneyFormStage.Destination
  } else {
    return JourneyFormStage.Waypoint
  }
}

function guessPlaceFlightType(place: PlaceType | null) {
  if (place && place.isAirportStop) {
    if (place.address.indexOf("Domestic") !== -1) {
      return FlightTypeEnum.Domestic
    }
    if (place.address.indexOf("International") !== -1) {
      return FlightTypeEnum.International
    }
  }
  return null
}

function onPlaceSelect(
  mapState: MapState<PlaceState>,
  stage: JourneyFormStage,
  place: PlaceType,
): void {
  mapState((state) => {
    const {journey, airportPickup, airportDropoff} = state
    switch (stage) {
      // If the place is an airport _and_ has a matching flight type, reset the dropdown to that.
      // You could argue we are storing the same info twice.  But consider Perth where T1/2 is one
      // place and T3/4 is another and yet domestic and international flights depart from each...
      // So this is just a helpful default for when the airport common place specifies Domestic or
      // International in its label (ie. address) field.
      case JourneyFormStage.Origin:
        return {
          ...state,
          journey: {...journey, origin: place},
          airportPickup: {
            ...airportPickup,
            flightType: guessPlaceFlightType(place) || airportPickup.flightType,
          },
        }
      case JourneyFormStage.Destination:
        return {
          ...state,
          journey: {...journey, destination: place},
          airportDropoff: {
            ...airportDropoff,
            flightType: guessPlaceFlightType(place) || airportPickup.flightType,
          },
        }
      case JourneyFormStage.Waypoint:
        return {...state, journey: {...journey, waypoints: [...journey.waypoints, place]}}
    }
  })
}



// WEBPACK FOOTER //
// ./src/components/PlaceFields.tsx